import React, { useMemo } from 'react'
import { useGeneralSchemaQuery } from './hooks/use-general-schema-query'
import { useMultiStepFormQuery } from './hooks/use-multi-step-form-query'
import { usePageSpecificSchemaQuery } from './hooks/use-page-specific-schema-query'
import { usePostHeroDefaultQuery } from './hooks/use-post-hero-default-query'
import { useHeaderQuery } from './hooks/use-header-query'
import favicon from './images/favicon.png'
import { useCallout1Query } from './hooks/use-callout-1-query'
import { useServiceAreasQuery } from './hooks/use-service-areas-query'
import { useContactQuery } from './hooks/use-contact-query'
import { useCouponSectionQuery } from './hooks/use-coupon-section-query'
import { useContentSection2ImageDefaultQuery } from './hooks/use-content-section-2-image-default'
import { useCallout2Query } from './hooks/use-callout2-query'
import { useFooterQuery } from './hooks/use-footer-query'
import { ReactSVG } from 'react-svg'
import { useAccoladesQuery } from './hooks/use-accolades-query'
import { useHomeHeroQuery } from './hooks/use-home-hero-query'
import { useOurServicesQuery } from './hooks/use-our-services-query'
import { useCallout3Query } from './hooks/use-callout-3-query'
import { useFeaturedServicesQuery } from './hooks/use-featured-services-query'
import { useTestimonialsSectionQuery } from './hooks/use-testimonials-section-query'
import { useNewsQuery } from './hooks/use-news-query'
import { useInteriorAccordionQuery } from './hooks/use-interior-accordion-query'

export function GeneralSchemaFunction() {
  const { wp, site, query1, forRatingValuePart1, forRatingValuePart2 } = useGeneralSchemaQuery()

  const data = wp.nexvelSchemaMarkup.nexvelschema

  return {
    services: data.services,
    cities: data.cities,
    businessName: data.businessName,
    siteUrl: site.siteMetadata.siteUrl,
    businessType: data.businessType,
    logo: data.logo.localFile.publicURL,
    image: data.image.localFile.publicURL,
    businessDescription: data.describeYourBusiness,
    phoneNumber: data.phoneNumber,
    address: data.streetAddress,
    city: data.city,
    state: data.state,
    zip: data.zipCode,
    openSunday: data.hoursOfOperation.sunday.areYouOpen,
    sundayClosingTime: `${data.hoursOfOperation.sunday.close.closeHour}:${data.hoursOfOperation.sunday.close.closeMinute} ${data.hoursOfOperation.sunday.close.amorpm}`,
    sundayOpeningTime: `${data.hoursOfOperation.sunday.open.openHour}:${data.hoursOfOperation.sunday.open.openMinute} ${data.hoursOfOperation.sunday.open.amorpm}`,
    openMonday: data.hoursOfOperation.monday.areYouOpen,
    mondayClosingTime: `${data.hoursOfOperation.monday.close.closeHour}:${data.hoursOfOperation.monday.close.closeMinute} ${data.hoursOfOperation.monday.close.amorpm}`,
    mondayOpeningTime: `${data.hoursOfOperation.monday.open.openHour}:${data.hoursOfOperation.monday.open.openMinute} ${data.hoursOfOperation.monday.open.amorpm}`,
    openTuesday: data.hoursOfOperation.tuesday.areYouOpen,
    tuesdayClosingTime: `${data.hoursOfOperation.tuesday.close.closeHour}:${data.hoursOfOperation.tuesday.close.closeMinute} ${data.hoursOfOperation.tuesday.close.amorpm}`,
    tuesdayOpeningTime: `${data.hoursOfOperation.tuesday.open.openHour}:${data.hoursOfOperation.tuesday.open.openMinute} ${data.hoursOfOperation.tuesday.open.amorpm}`,
    openWednesday: data.hoursOfOperation.wednesday.areYouOpen,
    wednesdayClosingTime: `${data.hoursOfOperation.wednesday.close.closeHour}:${data.hoursOfOperation.wednesday.close.closeMinute} ${data.hoursOfOperation.wednesday.close.amorpm}`,
    wednesdayOpeningTime: `${data.hoursOfOperation.wednesday.open.openHour}:${data.hoursOfOperation.wednesday.open.openMinute} ${data.hoursOfOperation.wednesday.open.amorpm}`,
    openThursday: data.hoursOfOperation.thursday.areYouOpen,
    thursdayClosingTime: `${data.hoursOfOperation.thursday.close.closeHour}:${data.hoursOfOperation.thursday.close.closeMinute} ${data.hoursOfOperation.thursday.close.amorpm}`,
    thursdayOpeningTime: `${data.hoursOfOperation.thursday.open.openHour}:${data.hoursOfOperation.thursday.open.openMinute} ${data.hoursOfOperation.thursday.open.amorpm}`,
    openFriday: data.hoursOfOperation.friday.areYouOpen,
    fridayClosingTime: `${data.hoursOfOperation.friday.close.closeHour}:${data.hoursOfOperation.friday.close.closeMinute} ${data.hoursOfOperation.friday.close.amorpm}`,
    fridayOpeningTime: `${data.hoursOfOperation.friday.open.openHour}:${data.hoursOfOperation.friday.open.openMinute} ${data.hoursOfOperation.friday.open.amorpm}`,
    openSaturday: data.hoursOfOperation.saturday.areYouOpen,
    saturdayClosingTime: `${data.hoursOfOperation.saturday.close.closeHour}:${data.hoursOfOperation.saturday.close.closeMinute} ${data.hoursOfOperation.saturday.close.amorpm}`,
    saturdayOpeningTime: `${data.hoursOfOperation.saturday.open.openHour}:${data.hoursOfOperation.saturday.open.openMinute} ${data.hoursOfOperation.saturday.open.amorpm}`,
    reviewsForRatingValuePart1:
      forRatingValuePart1?.nodes.length &&
      forRatingValuePart1.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsForRatingValuePart2:
      forRatingValuePart2?.nodes.length &&
      forRatingValuePart2.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsQuery:
      query1?.nodes.length &&
      query1.nodes.map((review, i) => {
        return {
          index: i,
          id: review.id,
          sourceType: review.sourceType,
          reviewDate: review.reviewDate,
          rating: review.rating,
          reviewerFirstName: review.reviewer.firstName,
          reviewerLastName: review.reviewer.lastName,
          comments: review.comments,
          businessName: review.businessName,
        }
      }),
    offers: data.offersSchema?.map(offer => {
      return {
        offer: offer.offerSchema,
        url: offer.urlSchema,
      }
    }),
    awards: data.awardsSchema?.map(award => {
      return {
        award: award.awardSchema,
      }
    }),
    brands: data.brandsSchema?.map(brand => {
      return {
        brand: brand.brandSchema,
      }
    }),
    certifications: data.certificationsSchema?.map(certification => {
      return {
        certification: certification.certificationSchema,
      }
    }),
    languages: data.languagesSchema?.map(language => {
      return {
        language: language.languageSchema,
      }
    }),
    organizations: data.organizationsSchema?.map(organization => {
      return {
        organization: organization.organizationSchema,
      }
    }),
    socialProfilesDirectoryListings: data.socialMediaProfilesAndDirectoryListingsSchema?.map(
      item => {
        return {
          socialProfileDirectory: item.socialMediaProfileOrDirectoryListingSchema,
        }
      },
    ),
  }
}
export function SeoFunction(data) {
  const seoData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  return {
    page:
      // eslint-disable-next-line no-nested-ternary
      data.wpPost !== undefined
        ? 'blogPost'
        : data.sitePage?.path === '/blog/'
          ? 'blogIndexPage'
          : null,
    favicon,
    title: seoData?.seo.title,
    metaDescription: seoData?.seo.metaDesc,
    ogTitle: seoData?.seo.opengraphTitle,
    ogDescription: seoData?.seo.opengraphDescription,
    ogUrl: seoData?.seo.opengraphUrl,
    ogSiteName: seoData?.seo.opengraphSiteName,
    ogType: seoData?.seo.opengraphType,
    ogImage: seoData?.seo.opengraphImage?.localFile.publicURL,
    canonicalUrl:
      seoData?.seo.canonical !== ''
        ? seoData?.seo.canonical
        : data.site.siteMetadata.siteUrl + seoData.uri,
    twitterTitle: seoData?.seo.twitterTitle,
    twitterDescription: seoData?.seo.twitterDescription,
    twitterImage: seoData?.seo.twitterImage?.localFile.childImageSharp.gatsbyImageData,
  }
}
export function PageSpecificSchemaFunction(data) {
  const pageData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  const pageSpecificSchemaData = usePageSpecificSchemaQuery().wp

  return {
    businessName: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessName,
    businessType: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessType,
    phoneNumber: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.phoneNumber,
    streetAddress: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.streetAddress,
    city: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.city,
    state: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.state,
    zipCode: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.zipCode,
    logo: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.logo.localFile.publicURL,
    blogPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.blogPageUrl,
    aboutPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.aboutPageUrl,
    contactPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.contactPageUrl,
    siteUrl: data.site.siteMetadata.siteUrl,
    uri: pageData !== undefined ? pageData.uri : '/blog/',
    title: pageData !== undefined ? pageData.title : 'Blog',
    datePublished: pageData?.date,
    dateModified: pageData?.modified,
    image:
      pageData?.seo?.opengraphImage !== null
        ? // eslint-disable-next-line no-unsafe-optional-chaining
          data.site.siteMetadata.siteUrl + pageData?.seo?.opengraphImage.localFile.publicURL
        : '/blank.jpg',
    author: `${pageData?.author?.node.firstName} ${pageData?.author?.node.lastName}`,
    categories:
      pageData?.categories?.nodes.length &&
      pageData?.categories.nodes.map((category, i) => {
        return {
          index: i,
          name: category.name,
        }
      }),
    tags:
      pageData?.tags?.nodes.length &&
      pageData?.tags.nodes.map((tag, i) => {
        return {
          index: i,
          name: tag.name,
        }
      }),
    articleBody: pageData?.content,
    post: data.wpPost !== undefined,
    videos:
      pageData?.nexvelschemapagesposts?.videos?.length &&
      pageData?.nexvelschemapagesposts?.videos.map((video, i) => {
        return {
          index: i,
          title: video.title,
          url: video.url,
          description: video.description,
          thumbnailImage: video.thumbnailImage.localFile.publicURL,
          uploadDate: video.uploadDate,
        }
      }),
    questionsAndAnswers:
      pageData?.nexvelschemapagesposts?.questionsAndAnswers?.length &&
      pageData?.nexvelschemapagesposts?.questionsAndAnswers.map((qanda, i) => {
        return {
          index: i,
          question: qanda.question,
          answer: qanda.answer,
        }
      }),
    maps:
      pageData?.nexvelschemapagesposts?.maps?.length &&
      pageData?.nexvelschemapagesposts?.maps.map((map, i) => {
        return {
          index: i,
          url: map.mapUrl,
        }
      }),
    digitalDocuments:
      pageData?.nexvelschemapagesposts?.digitaldocuments?.length &&
      pageData?.nexvelschemapagesposts?.digitaldocuments.map((document, i) => {
        return {
          index: i,
          title: document.title,
        }
      }),
    images:
      pageData?.nexvelschemapagesposts?.images?.length &&
      pageData?.nexvelschemapagesposts?.images.map((item, i) => {
        return {
          index: i,
          image: item.image.localFile.publicURL,
          date: item.image.date,
          description: item.image.description,
          title: item.image.title,
        }
      }),
    areaServedGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.cities.map(city => {
      return {
        city: city.city,
      }
    }),
    areaServedPageSpecific: pageData?.nexvelschemapagesposts?.areaServedLandingPageSchema,
    serviceGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.services.map(service => {
      return {
        serviceName: service.service,
        relevantPage: service.linkToRelevantPageOnYourWebsite,
      }
    }),
    servicePageSpecific: {
      serviceName: pageData?.nexvelschemapagesposts?.serviceSchema.serviceNameSchema,
      relevantPage: pageData?.nexvelschemapagesposts?.serviceSchema.linkToRelevantServicePageSchema,
    },
    keywords: pageData?.nexvelschemapagesposts?.keywordsSchema?.map(item => {
      return {
        keyword: item.keywordSchema,
      }
    }),
    localAwards: pageData?.nexvelschemapagesposts?.localAwardsSchema?.map(item => {
      return {
        localAward: item.localAwardSchema,
      }
    }),
    latitude: pageData?.nexvelschemapagesposts?.latitudeSchema,
    longitude: pageData?.nexvelschemapagesposts?.longitudeSchema,
    relevantExternalPages: pageData?.nexvelschemapagesposts?.relevantExternalPagesSchema?.map(
      item => {
        return {
          relevantExternalPage: item.relevantExternalPageSchema,
        }
      },
    ),
  }
}
export function MultiStepFormFunction() {
  const { wp } = useMultiStepFormQuery()
  const form = wp.acfOptionsMultiStepForm.multiStepForm
  return {
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
      width: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.width,
      height: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.height,
    },
    stepTitles: form.stepTitles.map(stepTitle => {
      return {
        title: stepTitle.stepTitle,
      }
    }),
    van: {
      src: `/.netlify/images?url=${form.vanImage.sourceUrl}`,
      alt: form.vanImageAltText,
      width: form.vanImage.mediaDetails.width,
      height: form.vanImage.mediaDetails.height,
    },
    residentialOrCommercial: form.residentialOrCommercial.map(option => {
      return {
        option: option.option,
      }
    }),
    issues: form.issues.map(issue => {
      return {
        issue: issue.issue,
        subIssues: issue.subIssues,
      }
    }),
    moreInfo: form.moreInfo,
    photoText: form.photoText,
    maxUploadText: form.maxUploadText,
    ourPhoneNumber: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    confirmationText: form.confirmationText,
    formSubmittedText: form.formSubmittedText,
    formAction: '/.netlify/functions/form-submission',
    bgColor: 'bg-white',
    borderColor: 'border-gray-darker',
    inputBorderColor: '[&>input]:border-gray-darker',
    inputTextColor: '[&>input]:text-black [&>input]:mt-2',
    placeholderTextColor: '[&>input]:placeholder:text-black',
    continueButtonDisabled: 'text-gray fill-gray',
    continueButton: 'text-black fill-black',
    continueButtonBorder: 'border-black',
    continueButtonBorderDisabled: 'border-gray',
    stepsColor: 'text-white',
    stepsBgColor: 'bg-blue',
    stepMarkerColorActive: 'bg-orange',
    stepMarkerColor: 'bg-gray',
    selectArrow: 'bg-blue',
    selectArrowDisabled: 'bg-gray',
    selectColor: 'text-black',
    paragraphColor: 'text-black',
    uploadBgColor: 'bg-white',
    arrowColor: 'bg-white',
    whatConvertsFormId: 116815,
  }
}
export function BlogPostFunction(data) {
  const defaultImg = usePostHeroDefaultQuery()
  return {
    image: {
      src:
        data.wpPost.featuredImage !== null
          ? `/.netlify/images?url=${data.wpPost.featuredImage.node.sourceUrl}`
          : `/.netlify/images?url=${defaultImg.heroImageDefault.sourceUrl}`,
      width:
        data.wpPost.featuredImage !== null
          ? data.wpPost.featuredImage.node.mediaDetails.width
          : defaultImg.heroImageDefault.mediaDetails.width,
      height:
        data.wpPost.featuredImage !== null
          ? data.wpPost.featuredImage.node.mediaDetails.height
          : defaultImg.heroImageDefault.mediaDetails.height,
      alt:
        data.wpPost.featuredImage !== null && data.wpPost.postFeaturedImageAltText !== undefined
          ? data.wpPost.postFeaturedImageAltText.altText
          : defaultImg.heroImageDefaultAltText,
    },
    blogTitle: data.wpPost.title,
    content: data.wpPost.content,
    prevButtonTitle: 'BACK TO PREV',
    nextButtonTitle: 'NEXT POST',
    outerBgColor: 'bg-white',
  }
}
export function BlogOverviewFunction(data) {
  const defaultImg = usePostHeroDefaultQuery()
  return {
    blogList: data.allWpPost.nodes.map(post => {
      return {
        image:
          post.featuredImage !== null
            ? `/.netlify/images?url=${post.featuredImage.node.sourceUrl}`
            : `/.netlify/images?url=${defaultImg.heroImageDefault.sourceUrl}`,
        width:
          post.featuredImage !== null
            ? post.featuredImage.node.mediaDetails.width
            : defaultImg.heroImageDefault.mediaDetails.width,
        height:
          post.featuredImage !== null
            ? post.featuredImage.node.mediaDetails.height
            : defaultImg.heroImageDefault.mediaDetails.height,
        alt:
          post.featuredImage !== null && post.postFeaturedImageAltText !== undefined
            ? post.postFeaturedImageAltText.altText
            : defaultImg.heroImageDefaultAltText,
        createdAt: post.date,
        title: post.title,
        description: post.excerpt,
        link: `/blog${post.link}`,
      }
    }),
    blogCardStyles: {
      borderColor: 'border-gray',
      dateBgColor: 'bg-blue',
      dateColor: 'text-white',
      titleColor: 'text-black',
    },
    postsPerPage: data.wp.allSettings.readingSettingsPostsPerPage,
    outerBgColor: 'bg-white',
    arrowColor: '#0C1F4A',
    paginationTextColor: 'text-blue-dark',
  }
}

export function HeaderFunction(handleButton) {
  const { wp, mainMenuDropdownImages, mainMenu, forRatingValuePart1, forRatingValuePart2 } =
    useHeaderQuery()

  function dropdownImg(a, b) {
    if (b.filter(e => e.associatedDropdown === a).length > 0) {
      const image2 = b.filter(e => e.associatedDropdown === a)

      return image2
    }
    return false
  }

  const menuImages = mainMenuDropdownImages.mainMenuImages.imageContainer.map(image => {
    return {
      src: `/.netlify/images?url=${image.image.sourceUrl}`,
      alt: image.altText,
      associatedDropdown: image.whichMenuItemIsThisAssociatedWith,
    }
  })

  function length(obj) {
    return Object.keys(obj).length
  }
  const averageRating = useMemo(() => {
    const reviewsRatings = {
      reviewsForRatingValuePart1:
        forRatingValuePart1?.nodes.length &&
        forRatingValuePart1.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
      reviewsForRatingValuePart2:
        forRatingValuePart2?.nodes.length &&
        forRatingValuePart2.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
    }

    const fiveFourThreeRatings = length(reviewsRatings.reviewsForRatingValuePart2)

    const totalRatings = length(reviewsRatings.reviewsForRatingValuePart1)

    const ratingsTotal = reviewsRatings.reviewsForRatingValuePart1

    const total = ratingsTotal.reduce((sum, item) => sum + item.rating, 0)

    let avgRating = total / fiveFourThreeRatings
    avgRating = avgRating.toFixed(1)

    return [avgRating, totalRatings]
  }, [forRatingValuePart1, forRatingValuePart2])

  return {
    bgColor: 'bg-white',
    navLogo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      height: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.height,
      width: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.width,
    },
    navLogoAltText: wp.acfOptionsCommonItems.commonItems.logoAltText,
    rate: averageRating[0],
    starPrimary: '#F87903',
    starPrimaryMobile: '#F87903',
    starSecondary: '#dddddd',
    reviewCnt: averageRating[1],
    reviewsLink: {
      path: wp.acfOptionsCommonItems.commonItems.reviewLink.url,
      label: wp.acfOptionsCommonItems.commonItems.reviewLink.title,
    },
    callButtonProps: {
      rounded: false,
      twoLabels: true,
      label: wp.acfOptionsCommonItems.commonItems.topCallText,
      label2: wp.acfOptionsCommonItems.commonItems.phoneNumber,
      notLink: false,
      fontWeight: 'font-normal',
      fontSize: '3xl:text-base xl:text-[0.75rem] lg:text-[0.53331rem]',
      fontFamily: 'font-primary',
      textColor: 'text-white',
      label2Color: 'text-orange',
      borderColor: 'border-blue',
      bgColor: 'bg-blue h-full',
      hoverBgColor: 'hover:bg-blue-darkest',
      hoverBorderColor: 'hover:border-blue-darkest',
      hoverTextColor: 'hover:text-white',
      padding: 'py-0',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      phoneIcon: true,
      iconColor: '#F87903',
    },
    quoteButtonProps: {
      rounded: false,
      notLink: true,
      label: wp.acfOptionsCommonItems.commonItems.requestButtonText,
      borderColor: 'border-white',
      fontWeight: 'font-black',
      fontSize: '3xl:text-base xl:text-[0.75rem] lg:text-[0.53331rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-white h-full',
      textColor: 'text-orange',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      padding: 'py-0',
      onClick: handleButton,
    },
    menuData: mainMenu.nodes.map(topLevel => {
      return {
        label: topLevel.label,
        link: topLevel.path,
        image: dropdownImg(topLevel.label, menuImages)[0]?.src,
        subMenu: topLevel.childItems.nodes.length
          ? topLevel.childItems.nodes.map(secondLevel => {
              return {
                label: secondLevel.label,
                link: secondLevel.path,
                subMenu: secondLevel.childItems.nodes.length
                  ? secondLevel.childItems.nodes.map(thirdLevel => {
                      return {
                        label: thirdLevel.label,
                        link: thirdLevel.path,
                        subMenu: [],
                      }
                    })
                  : [],
              }
            })
          : [],
      }
    }),
    topBgColor: 'bg-blue',
    topBorderColor: 'border-blue',
    textColor: 'text-white',
    reviewTextColor: 'text-white',
    bottomBgColor: 'bg-orange',
    borderColor: 'border-white',
    mobileCallButton: {
      rounded: true,
      notLink: false,
      borderColor: 'border-orange',
      bgColor: 'bg-orange',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      phoneIcon: true,
      iconColor: '#Ffffff',
    },
    onRequestServiceClick: handleButton,
    phoneNumber: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    mobileBgColor: 'bg-orange',
    mobileSubBgColor: 'bg-orange',
    mobileTextColor: 'text-white',
    mobileHeaderBgColor: 'bg-white',
    mobileCloseTextColor: 'text-black',
    mobileDividerBorderColor: 'border-white/50',
    mobileFontWeight: 'font-black',
    mobileSubFontWeight: 'font-black',
    breadCrumbDivider: '/',
    breadCrumbHomeLabel: 'Home',
    breadCrumbTextColor: 'text-white',
    mobileCallButtonProps: {
      notLink: false,
      outline: true,
      phone: true,
      text: wp.acfOptionsCommonItems.commonItems.phoneNumber,
      fontSize: 'text-[1.125rem]',
      bgColor: 'bg-blue',
      hoverBg: 'hover:bg-blue-darkest',
      hoverTextColor: 'hover:text-white',
      fillColor: 'hover:svgPurple',
      textColor: 'text-white',
      borderColor: 'border-blue',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
    },
    mobileRequestButtonProps: {
      notLink: true,
      outline: true,
      phone: false,
      text: wp.acfOptionsCommonItems.commonItems.requestButtonText,
      fontSize: 'text-[1.125rem] py-1',
      bgColor: 'bg-white',
      hoverBg: 'hover:bg-orange',
      hoverTextColor: 'hover:text-white',
      textColor: 'text-orange',
      borderColor: 'border-white',
      onClick: handleButton,
    },
    megaMenuBgColor: 'bg-orange',
    megaMenuImageBgColor: 'bg-blue',
    menuRequestButton: {
      rounded: false,
      notLink: true,
      label: wp.acfOptionsCommonItems.commonItems.requestButtonText,
      borderColor: 'border-white',
      fontWeight: 'font-black',
      fontSize: 'text-xs rfs:xl:text-2xl',
      fontFamily: 'font-primary',
      bgColor: 'bg-white',
      textColor: 'text-orange',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5 px-0',
      onClick: handleButton,
    },
    megaMenuLogo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logoWhite.sourceUrl}`,
      height: wp.acfOptionsCommonItems.commonItems.logoWhite.mediaDetails.height,
      width: wp.acfOptionsCommonItems.commonItems.logoWhite.mediaDetails.width,
    },
    megaMenuLogoAltText: wp.acfOptionsCommonItems.commonItems.logoAltText,
    socials: [],
    iconFillColor: 'fill-white',
    megaMenuTextColor: 'text-white',
    megaMenuHoverTextColor: 'hover:text-orange',
    megaMenuHoverBgColor: 'hover:bg-white',
    megaMenuBorderColor: 'border-white',
    megaMenuSubitemBgColor: '!bg-orange',
    megaMenuSubitemHoverBgColor: 'hover:!bg-blue',
    megaMenuSubitemTextColor: '!text-white',
    megaMenuSubitemHoverTextColor: 'hover:!text-white',
  }
}

export function BreadcrumbFunction(data) {
  return {
    breadcrumbs: data.wpPage.breadcrumbs.breadcrumbs.breadcrumbs,
  }
}

export function InteriorHeadingsFunction(data) {
  return {
    borderColor: 'border-orange',
    contentSection: data.wpPage.interiorHeadings.interiorHeadings.headings,
  }
}

export function ContentSection1Function(data) {
  return {
    contentSection: data.wpPage.contentSection1.contentSection1,
  }
}

export function InteriorCardsFunction(data) {
  return {
    cardData: data.wpPage.interiorCards.interiorCards.cards.map(card => {
      return {
        icon: card.icon.localFile.publicURL,
        label: card.title,
        content: card.text,
      }
    }),
    iconFillColor: 'fill-white',
    iconBgColor: 'bg-orange',
    iconBorderColor: 'border-blue',
    labelColor: 'text-blue',
    textColor: 'text-black',
    readMoreColor: 'text-orange',
    borderColor: 'border-orange',
  }
}

export function CalloutV12Function(handleButton) {
  const data = useCallout1Query()
  return {
    bgImage: `/.netlify/images?url=${data.callout1.backgroundImageDesktop.sourceUrl}`,
    bgImageMobile: `/.netlify/images?url=${data.callout1.backgroundImageMobile.sourceUrl}`,
    bgGradient: 'bg-gradient-to-b lg:bg-gradient-to-r from-blue via-blue/[92%] to-blue/50 ',
    title: data.callout1.title,
    buttonProps: {
      rounded: false,
      notLink: true,
      label: data.callout1.buttonText,
      borderColor: 'border-orange',
      fontWeight: 'font-black',
      fontSize: 'text-[1.25rem] sm:text-[1.875rem] md:text-[2.5rem] lg:rfs:text-[1.83331rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-orange',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-blue-light',
      hoverBorderColor: 'hover:border-blue-light',
      hoverTextColor: 'hover:text-white',
      padding: 'py-4 px-0',
      onClick: handleButton,
    },
    textColor: 'text-white',
  }
}

export function ServiceAreasFunction(handleButton) {
  const data = useServiceAreasQuery()
  return {
    bgColor: 'bg-white',
    bgImg: `/.netlify/images?url=${data.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: `/.netlify/images?url=${data.backgroundImageMobile.sourceUrl}`,
    containerBg: `/.netlify/images?url=${data.containerBackgroundImage.sourceUrl}`,
    containerBgMobile: `/.netlify/images?url=${data.containerBackgroundMobile.sourceUrl}`,
    title: data.title,
    subtitle: data.subtitle,
    image: {
      src: `/.netlify/images?url=${data.image.sourceUrl}`,
      width: data.image.mediaDetails.width,
      height: data.image.mediaDetails.height,
    },
    imageAltText: data.imageAltText,
    content: data.text,
    tabs: data.states.map(state => {
      return {
        serviceAreasStateName: state.state,
        serviceAreasCounties: state.countiesAndTowns.map(county => {
          return {
            serviceAreasCountyName: county.county,
            serviceAreasTowns: county.towns,
          }
        }),
      }
    }),
    lineBorderColor: 'border-orange',
    containerBgColor: 'bg-blue-darkest',
    titleColor: 'text-black',
    textColor: 'text-white',
    buttonProps: {
      rounded: false,
      notLink: true,
      label: data.buttonText,
      borderColor: 'border-orange',
      fontWeight: 'font-black',
      fontSize: 'text-lg lg:rfs:text-[1.33313rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-orange',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'py-5 px-0',
      onClick: handleButton,
    },
  }
}

export function ContactFunction() {
  const data = useContactQuery()
  return {
    lessOpacity: true,
    title: data.title,
    subtitle: data.subtitle,
    bgImg: `/.netlify/images?url=${data.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: `/.netlify/images?url=${data.backgroundImageMobile}`,
    textColor: 'text-white',
    subtitleColor: 'text-white',
    bgColor: 'bg-orange',
    form: {
      textColor: '[&>input]:placeholder:text-white placeholder:text-white text-white',
      borderColor: '[&>input]:border-white',
      actionUrl: '/thank-you/',
      submitButton: {
        rounded: false,
        hasArrow: false,
        notLink: true,
        primaryFont: true,
        label: data.submitButtonText,
        bgColor: 'bg-white',
        fontSize: 'text-[1.125rem] lg:rfs:text-[1.5rem] !py-4 !px-8',
        textColor: 'text-blue',
        hoverBg: 'hover:bg-blue',
        hoverTextColor: 'hover:text-white',
      },
    },
  }
}
export function CouponFunction() {
  const { wpPage, wp } = useCouponSectionQuery()
  const data = wpPage.couponSection.couponSection.coupons
  return {
    couponDescription: data[0].text,
    finePrint: data[0].finePrint,
    finePrintColor: 'text-white',
    borderColor: 'border-gray',
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
      width: '100%',
      height: 'auto',
    },
    topBgColor: 'bg-white',
    textColorTop: 'text-gray-darker',
    textColorBottom: 'text-white',
    percentageOrFixedAmount: data[0].percentOrFixedAmount,
    amountOff: data[0].amount,
    bottomBgColor: 'bg-orange',
    fillColor: 'fill-white',
    printButtonProps: {
      label: wpPage.couponSection.couponSection.printButtonText,
      notLink: true,
      borderColor: 'border-blue',
      fontWeight: 'font-[900]',
      fontSize: 'rfs:text-[2.05875rem] lg:rfs:text-[1.5rem]',
      textColor: 'text-white',
      bgColor: 'bg-blue',
      hoverBgColor: 'hover:bg-white',
      hoverBorderColor: 'hover:border-white',
      hoverTextColor: 'hover:text-gray-darker',
      icon: false,
      twoLabels: false,
    },
    offColor: 'text-white',
    priceBorder: 'border-white',
    showOff: data[0].showOff,
    fontSecondary: true,
  }
}
export function FAQsFunction(data) {
  return {
    heading: data.wpPage.faqs.faqs.title,
    questions: data.wpPage.nexvelschemapagesposts.questionsAndAnswers.map(faq => {
      return {
        title: faq.question,
        content: faq.answer,
      }
    }),
    titleColor: 'text-white',
    tabBgColor: 'bg-orange',
    tabBorderColor: 'border-white',
    contentColor: 'text-white',
    iconColor: 'text-white',
    bgColor: 'bg-blue-darkest',
  }
}
export function ContentSection2Function(data) {
  const contentSection2 = data.wpPage.contentSection2.contentSection2
  const defaultImg = useContentSection2ImageDefaultQuery()
  return {
    title: contentSection2.title,
    text: contentSection2.text,
    textColor: contentSection2.hasAccordions === 'Yes' ? 'text-white' : 'text-black',
    img: {
      src:
        contentSection2.desktopImage !== null
          ? `/.netlify/images?url=${contentSection2.desktopImage.sourceUrl}`
          : `/.netlify/images?url=${defaultImg.contentSection2DesktopImageDefault.sourceUrl}`,
      width:
        contentSection2.desktopImage !== null
          ? contentSection2.desktopImage.mediaDetails.width
          : defaultImg.contentSection2DesktopImageDefault.mediaDetails.width,
      height:
        contentSection2.desktopImage !== null
          ? contentSection2.desktopImage.mediaDetails.height
          : defaultImg.contentSection2DesktopImageDefault.mediaDetails.height,
      alt:
        contentSection2.desktopImage !== null
          ? contentSection2.desktopImageAltText
          : defaultImg.contentSection2DesktopImageDefaultAltText,
    },
    hasAccordions: contentSection2.hasAccordions,
    accordions: contentSection2.accordions?.map(accordion => {
      return {
        label: accordion.label,
        text: accordion.text,
      }
    }),
    accordionBgColor: 'bg-blue',
    textNoAccordions: contentSection2.textNoAccordions,
    numberBg: 'bg-orange',
  }
}
export function Callout2Function(handleButton) {
  const data = useCallout2Query()
  return {
    vanImg: {
      src: `/.netlify/images?url=${data.van.sourceUrl}`,
      width: data.van.mediaDetails.width,
      height: data.van.mediaDetails.height,
      alt: data.vanAltText,
    },
    title: data.title,
    bgColor: 'bg-orange',
    textColor: 'text-white',
    button: {
      rounded: false,
      notLink: true,
      label: data.buttonText,
      borderColor: 'border-blue',
      fontWeight: 'font-black',
      fontSize: 'text-lg lg:text-lg xl:text-2xl',
      fontFamily: 'font-primary',
      bgColor: 'bg-blue',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-white',
      hoverBorderColor: 'hover:border-white',
      hoverTextColor: 'hover:text-black',
      padding: 'py-3 px-0',
      onClick: handleButton,
    },
  }
}
export function FooterFunction() {
  const { wp, footerMenu1, footerMenu2 } = useFooterQuery()
  const footer = wp.acfOptionsCommonItems.commonItems.footer
  return {
    bgColor: 'bg-blue-darkest',
    logo: {
      src: `/.netlify/images?url=${footer.footerLogo.sourceUrl}`,
      width: footer.footerLogo.mediaDetails.width,
      height: footer.footerLogo.mediaDetails.height,
      alt: footer.footerLogoAltText,
    },
    socialMedia: footer.socialMedia.map(social => {
      return {
        logo: <ReactSVG src={social.socialMediaIcon.localFile.publicURL} />,
        link: social.socialMediaLink.url,
      }
    }),
    menu1Title: footerMenu1.nodes[0].menu.node.name,
    menu1: footerMenu1.nodes.map(menu => {
      return {
        label: menu.label,
        path: menu.path,
      }
    }),
    menu2Title: footerMenu2.nodes[0].menu.node.name,
    menu2: footerMenu2.nodes.map(menu => {
      return {
        label: menu.label,
        path: menu.path,
      }
    }),
    contactLabel: footer.contactUs.title,
    contactText: footer.contactUs.text,
    officeHoursLabel: footer.officeHours.title,
    officeHours: footer.officeHours.text,
    copyright: footer.copyright,
    nexvel: {
      logo: <ReactSVG src={footer.nexvelLogo.sourceUrl} />,
      link: footer.nexvelLink.url,
    },
    headingColor: 'text-white',
    textColor: 'text-white',
    borderTopColor: 'border-gray-darker',
    triangleColor: '[&_path]:fill-orange',
  }
}

export function HomeHeroFunction(handleButton) {
  const data = useHomeHeroQuery()
  return {
    bgImgMobile: `/.netlify/images?url=${data.backgroundImageMobile.sourceUrl}`,
    bgImgDesktop: `/.netlify/images?url=${data.backgroundImageDesktop.sourceUrl}`,
    title: data.title,
    subtitle: data.subtitle,
    button: {
      rounded: false,
      notLink: true,
      label: data.buttonText,
      borderColor: 'border-orange',
      fontWeight: 'font-black',
      fontSize: 'text-base rfs:lg:text-[1.83331rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-orange',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5 px-0',
      onClick: handleButton,
    },
    textColor: 'text-white',
  }
}

export function OurServicesFunction(handleButton) {
  const { wpPage, wp } = useOurServicesQuery()
  return {
    bgImage: `/.netlify/images?url=${wpPage.ourServices.ourServices.backgroundImageDesktop.sourceUrl}`,
    bgColor: 'bg-blue-darkest',
    textColor: 'text-blue-lightest',
    titleColor: 'text-white',
    borderColor: 'border-gray/[20%]',
    title: wpPage.ourServices.ourServices.title,
    serviceCategories: wpPage.ourServices.ourServices.services.map(service => {
      return {
        icon: service.icon.localFile.publicURL,
        image: {
          src: `/.netlify/images?url=${service.image.sourceUrl}`,
          alt: service.altText,
          width: service.image.mediaDetails.width,
          height: service.image.mediaDetails.height,
        },
        label: service.label,
        title: service.title1,
        text: service.text,
        link: service.link !== null ? service.link.url : undefined,
        linkText: service.link !== null ? service.link.title : '',
        services:
          service.tabServices !== null
            ? service.tabServices.map(tab => {
                return {
                  icon: tab.icon.localFile.publicURL,
                  label: tab.label,
                  title: tab.title,
                  text: tab.content,
                  link: {
                    url: tab.link.url,
                    text: tab.link.title,
                  },
                }
              })
            : [],
      }
    }),
    activeCategoryIconColor: '[&_path]:fill-white',
    buttonHover: '[&_path]:hover:fill-orange hover:text-orange',
    buttonActive: 'border-transparent [&_path]:fill-orange text-orange',
    callButton: {
      rounded: false,
      twoLabels: true,
      label: wp.acfOptionsCommonItems.commonItems.topCallText,
      label2: wp.acfOptionsCommonItems.commonItems.phoneNumber,
      notLink: false,
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:xl:text-[1.5rem]',
      fontFamily: 'font-primary',
      textColor: 'text-white',
      label2Color: 'text-white',
      borderColor: 'border-orange',
      bgColor: 'bg-orange',
      hoverBgColor: 'hover:bg-orange-light2',
      hoverBorderColor: 'hover:border-orange-light2',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      phoneIcon: true,
      iconColor: '#FFFFFF',
    },
    estimateButton: {
      rounded: false,
      notLink: true,
      label: wp.acfOptionsCommonItems.commonItems.requestButtonText,
      borderColor: 'border-white',
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:xl:text-[1.5rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-white',
      textColor: 'text-orange',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5',
      onClick: handleButton,
    },
    learnMoreButtonColor:
      'border-white bg-[#0B0721] text-white hover:border-orange hover:text-orange',
    activeBorderColor: 'border-orange',
    buttonActiveDesktop: '[&_path]:fill-orange text-orange',
    selectEven: 'border-r-orange',
    selectOdd: 'border-l-orange',
    arrowActiveColor: '[&_path]:fill-orange',
  }
}

export function CalloutV14Function(handleButton) {
  const data = useCallout3Query()
  return {
    bgColor: 'bg-orange',
    title: data.title,
    titleColor: 'text-white',
    requestButtonProps: {
      rounded: false,
      notLink: true,
      label: data.requestButtonText,
      borderColor: 'border-white',
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:xl:text-[1.8rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-white',
      textColor: 'text-orange',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5',
      onClick: handleButton,
    },
    financingButtonProps: {
      rounded: false,
      notLink: false,
      label: data.link.title,
      borderColor: 'border-blue',
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:xl:text-[1.8rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-blue',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-blue-darkest',
      hoverBorderColor: 'hover:border-blue-darkest',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5',
      href: data.link.url,
    },
    image: {
      src: `/.netlify/images?url=${data.vanImage.sourceUrl}`,
      width: data.vanImage.mediaDetails.width,
      height: data.vanImage.mediaDetails.height,
    },
    imageAltText: data.vanImageAltText,
  }
}

export function FeaturedServicesFunction() {
  const data = useFeaturedServicesQuery()
  return {
    bgColor: 'bg-white',
    title: data.title,
    content: data.text,
    tabs: data.accordions.map(tab => {
      return {
        label: tab.label,
        content: tab.text,
        image: `/.netlify/images?url=${tab.image.sourceUrl}`,
        link: tab.link.url,
        buttonText: tab.link.title,
      }
    }),
    learnMoreButtonProps: {
      notLink: false,
      borderColor: 'border-orange',
      fontWeight: 'font-black',
      fontSize: 'text-base rfs:lg:text-[1.33331rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-orange',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-blue',
      hoverBorderColor: 'hover:border-blue',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5 px-0',
    },
    textColor: 'text-black',
    textColor2: 'text-blue-dark2',
    borderColor: 'border-gray-light',
    hoverBgColor: 'hover:bg-blue',
    hoverTextColor: 'hover:text-white',
    activeBgColor: '!bg-orange',
    activeTextColor: '!text-white',
    beforeBorderColor: 'before:border-blue',
    cardBgColor: 'bg-white',
    svgFillActive: 'fill-white',
    backdropColor: 'svgPathBlue',
  }
}
export function AccoladesFunction() {
  const data = useAccoladesQuery()
  return {
    title: data.title,
    text: data.text,
    accolades: data.accolades.map(accolade => {
      return {
        svg: `/.netlify/images?url=${accolade.image.localFile.publicURL}`,
        label: accolade.label,
      }
    }),
    accoladeHover: 'hover:bg-blue-light hover:!text-white',
    iconHovered: '#FFFFFF',
    titleColor: 'text-black',
    textColor: 'text-black',
    bgColor: 'bg-white',
    gapColor: 'bg-gray-lightest',
    activeImg: `/.netlify/images?url=${data.activeOverlay.localFile.publicURL}`,
  }
}
export function TestimonialsFunction() {
  const { wpPage, forRatingValuePart1, forRatingValuePart2, reviews } =
    useTestimonialsSectionQuery()
  const videoSection = wpPage.testimonialsSection.testimonialsSection.videoSection
  const testimonials = wpPage.testimonialsSection.testimonialsSection.testimonials

  function length(obj) {
    return Object.keys(obj).length
  }

  const averageRating = useMemo(() => {
    const reviewsRatings = {
      reviewsForRatingValuePart1:
        forRatingValuePart1?.nodes.length &&
        forRatingValuePart1.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
      reviewsForRatingValuePart2:
        forRatingValuePart2?.nodes.length &&
        forRatingValuePart2.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
    }

    const fiveFourThreeRatings = length(reviewsRatings.reviewsForRatingValuePart2)

    const totalRatings = length(reviewsRatings.reviewsForRatingValuePart1)

    const ratingsTotal = reviewsRatings.reviewsForRatingValuePart1

    const total = ratingsTotal.reduce((sum, item) => sum + item.rating, 0)

    let avgRating = total / fiveFourThreeRatings
    avgRating = avgRating.toFixed(1)

    return [avgRating, totalRatings]
  }, [forRatingValuePart1, forRatingValuePart2])

  return {
    videoSectionProps: {
      title: videoSection.title,
      backgroundImageMobile: `/.netlify/images?url=${videoSection.mobileImage.sourceUrl}`,
      backgroundImageDesktop: `/.netlify/images?url=${videoSection.desktopImage.sourceUrl}`,
      playButton: `/.netlify/images?url=${videoSection.playButton.localFile.publicURL}`,
      videoUrl: videoSection.videoUrl.url,
      title2: videoSection.title2,
      text: videoSection.text,
      link: videoSection.link.url,
      borderColor: 'border-orange',
      titleColor: 'text-black',
      textColor: 'text-white',
    },
    testimonialsProps: {
      title: testimonials.title,
      subtitle: testimonials.subtitle,
      reviewsBadge: {
        reviewsWidgetText: testimonials.reviewWidgetText,
        rate: averageRating[0],
        reviewCnt: averageRating[1],
        bgColor: 'bg-blue',
        innerBorderColor: 'before:border-white',
        textColor: 'text-white',
        instance: 1,
        emptyStarColor: 'fill-gray-light',
        starColor: 'fill-orange-light',
        reviewsRatings: {
          reviewsForRatingValuePart1:
            forRatingValuePart1?.nodes.length &&
            forRatingValuePart1.nodes.map((rating, i) => {
              return {
                index: i,
                rating: rating.rating,
              }
            }),
          reviewsForRatingValuePart2:
            forRatingValuePart2?.nodes.length &&
            forRatingValuePart2.nodes.map((rating, i) => {
              return {
                index: i,
                rating: rating.rating,
              }
            }),
        },
      },
      review: {
        firstName: reviews.nodes[0].reviewer.firstName,
        lastName: reviews.nodes[0].reviewer.lastName,
        logoImage: reviews.nodes[0].sourceType,
        content: reviews.nodes[0].comments,
      },
      google: {
        logo: 'Google',
        src: `/.netlify/images?url=${testimonials.reviewIcons.googleIcon.sourceUrl}`,
        width: testimonials.reviewIcons.googleIcon.mediaDetails.width,
        height: testimonials.reviewIcons.googleIcon.mediaDetails.height,
      },
      facebook: {
        logo: 'Facebook',
        src: `/.netlify/images?url=${testimonials.reviewIcons.facebookIcon.sourceUrl}`,
        width: testimonials.reviewIcons.facebookIcon.mediaDetails.width,
        height: testimonials.reviewIcons.facebookIcon.mediaDetails.height,
      },
      link: {
        title: testimonials.link.title,
        url: testimonials.link.url,
        textColor: 'text-white',
        bgColor: 'bg-orange',
        hover: 'hover:bg-blue active:bg-blue',
      },
      decorativeSvg: `/.netlify/images?url=${testimonials.decorativeSvg.localFile.publicURL}`,
      images: testimonials.images.map(image => {
        return {
          mobileImg: {
            src: `/.netlify/images?url=${image.mobileImage.sourceUrl}`,
            width: image.mobileImage.mediaDetails.width,
            height: image.mobileImage.mediaDetails.height,
            alt: image.altText,
          },
          desktopImg: {
            src: `/.netlify/images?url=${image.desktopImage.sourceUrl}`,
            width: image.desktopImage.mediaDetails.width,
            height: image.desktopImage.mediaDetails.height,
            alt: image.altText,
          },
        }
      }),

      imageBorderColor: 'border-white',
    },
  }
}
export function CouponsFunction() {
  const { wpPage, wp } = useCouponSectionQuery()
  const couponSection = wpPage.couponSection.couponSection
  return {
    bgColor: 'bg-blue-darkest',
    subtitle: couponSection.title,
    subtitleColor: 'text-white',
    paddingTopMobile: 'pt-[4.25rem]',
    buttonVersion: 'v14',
    isAjm: true,
    couponSlider: {
      isHome: true,
      mobileSlidesToShow: 1,
      mobileShowDots: true,
      hasButton: false,
      coupons: wpPage.couponSection.couponSection.coupons.map(coupon => {
        console.log(coupon)
        return {
          couponDescription: coupon.text,
          percentageOrFixedAmount: coupon.percentOrFixedAmount,
          amountOff: coupon.amount,
          showOff: coupon.showOff,
          finePrint: coupon.finePrint,
          finePrintColor: 'text-white',
        }
      }),
      couponProps: {
        borderColor: 'border-gray',
        isLogoSvg: false,
        logo: {
          src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
          alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
          width: '100%',
          height: 'auto',
        },
        topBgColor: 'bg-white',
        textColorTop: 'text-gray-darker',
        textColorBottom: 'text-white',
        bottomBgColor: 'bg-orange',
        fillColor: 'fill-white',
        printButtonProps: {
          label: couponSection.printButtonText,
          notLink: true,
          borderColor: 'border-blue',
          fontWeight: 'font-[900]',
          fontSize: 'rfs:text-[2.05875rem] lg:rfs:text-[1.5rem]',
          textColor: 'text-white',
          bgColor: 'bg-blue',
          hoverBgColor: 'hover:bg-white',
          hoverBorderColor: 'hover:border-white',
          hoverTextColor: 'hover:text-gray-darker',
          icon: false,
          twoLabels: false,
        },
        offColor: 'text-white',
        priceBorder: 'border-white',
        fontSecondary: true,
      },
    },
    link: {
      rounded: false,
      notLink: false,
      label: couponSection.link.title,
      borderColor: 'border-white',
      fontWeight: 'font-black',
      fontSize: 'text-lg lg:text-2xl',
      fontFamily: 'font-primary',
      bgColor: 'bg-white',
      textColor: 'text-orange',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      padding: 'py-5 px-0',
      href: couponSection.link.url,
    },
  }
}
export function BlogSectionFunction() {
  const { wpPage, allWpPost, wp } = useNewsQuery()
  return {
    recentBlogList: allWpPost.nodes.map(post => {
      return {
        image:
          post.featuredImage !== null
            ? `/.netlify/images?url=${post.featuredImage.node.sourceUrl}`
            : `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.post.heroImageDefault.sourceUrl}`,
        width: '100%',
        height: '',
        alt:
          post.postFeaturedImageAltText.altText !== null
            ? post.postFeaturedImageAltText.altText
            : wp.acfOptionsCommonItems.commonItems.post.heroImageDefaultAltText,
        createdAt: post.date,
        title: post.title,
        description: post.excerpt,
        link: `/blog${post.link}`,
      }
    }),
    title: wpPage.news.news.title,
    mobileTitle: wpPage.news.news.title,
    titleColor: 'text-blue-dark',
    blogCardStyles: {
      dateColor: 'text-orange',
      titleColor: 'text-blue-dark',
      bgColor: 'bg-white',
      textColor: 'text-blue-dark',
    },
    bgImage: `/.netlify/images?url=${wpPage.news.news.backgroundImage.sourceUrl}`,
    bgColor: 'bg-white',
  }
}

export function CouponPageFunction(coupon) {
  const { wpPage, wp } = useCouponSectionQuery()
  return {
    couponDescription: coupon.text,
    finePrint: coupon.finePrint,
    finePrintColor: 'text-white',
    borderColor: 'border-gray',
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
      width: '100%',
      height: 'auto',
    },
    topBgColor: 'bg-white',
    textColorTop: 'text-gray-darker',
    textColorBottom: 'text-white',
    percentageOrFixedAmount: coupon.percentOrFixedAmount,
    amountOff: coupon.amount,
    bottomBgColor: 'bg-orange',
    fillColor: 'fill-white',
    printButtonProps: {
      label: wpPage.couponSection.couponSection.printButtonText,
      notLink: true,
      borderColor: 'border-blue',
      fontWeight: 'font-[900]',
      fontSize: 'rfs:text-[2.05875rem] lg:rfs:text-[1.5rem]',
      textColor: 'text-white',
      bgColor: 'bg-blue',
      hoverBgColor: 'hover:bg-white',
      hoverBorderColor: 'hover:border-white',
      hoverTextColor: 'hover:text-gray-darker',
      icon: false,
      twoLabels: false,
    },
    offColor: 'text-white',
    priceBorder: 'border-white',
    showOff: 'true',
    fontSecondary: true,
  }
}
export function ContentSection3Function(data) {
  return {
    contentSection: data.wpPage.contentSection3.contentSection3,
  }
}

export function ContentSectionAccordionsFunction(data) {
  const defaultData = useInteriorAccordionQuery()
  return {
    textColor: 'text-black',
    bgColor: 'bg-white',
    borderColor: 'border-orange',
    contentData: data.wpPage.contentSectionAccordion.contentSectionAccordion.accordion.map(
      accordion => {
        return {
          title: accordion.label,
          content1: accordion.content1,
          content2: accordion.content2,
          image:
            accordion.image && accordion.image.sourceUrl
              ? `/.netlify/images?url=${accordion.image.sourceUrl}`
              : `/.netlify/images?url=${defaultData.defaultAccordionImage.sourceUrl}`,
        }
      },
    ),
    arrowIcon: defaultData.accordionArrow.sourceUrl,
  }
}
